/**
 * Shared across tabs: 10‑minute refresh cycle continues from login,
 * not from when each tab was opened.
 */
const REFRESH_ANCHOR_KEY = "fonemed-token-refresh-anchor";
const REFRESH_LOCK_KEY = "fonemed-token-refresh-lock";
const WEB_LOCK_NAME = "fonemed-token-refresh";
const REFRESH_LOCK_MS = 30_000;
const LOCK_RENEW_MS = 10_000; // renew well before REFRESH_LOCK_MS expires
const LOCK_VERIFY_DELAY_MS = 75;

function readAnchor() {
  try {
    const value = Number(localStorage.getItem(REFRESH_ANCHOR_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

/** Call on login so the shared 10‑minute refresh clock starts from login. */
export function recordTokenRefreshAnchor(at = Date.now()) {
  try {
    localStorage.setItem(REFRESH_ANCHOR_KEY, String(at));
  } catch {
    // ignore
  }
}

export function clearTokenRefreshAnchor() {
  try {
    localStorage.removeItem(REFRESH_ANCHOR_KEY);
  } catch {
    // ignore
  }
}

/**
 * Ensure an anchor exists. Never overwrite an existing login anchor.
 */
export function initializeTokenRefreshAnchor() {
  const existing = readAnchor();
  if (existing != null) {
    return existing;
  }
  const anchor = Date.now();
  recordTokenRefreshAnchor(anchor);
  return anchor;
}

/**
 * Ms until next tick on the shared schedule (login + 10m, +20m, …).
 */
export function getMsUntilNextTokenRefresh(intervalMs) {
  const anchor = initializeTokenRefreshAnchor();
  const elapsed = Math.max(0, Date.now() - anchor);
  const nextAt =
    anchor + (Math.floor(elapsed / intervalMs) + 1) * intervalMs;
  return Math.max(0, nextAt - Date.now());
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function generateLockId(tabId) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${tabId}-${crypto.randomUUID()}`;
  }
  return `${tabId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * localStorage fallback when Web Locks API is unavailable.
 * Write → short wait → re-read so only one tab keeps the lock.
 *
 * @returns {Promise<string|null>} the lockId this tab owns, or null if it
 * did not acquire the lock (including on storage errors — fails CLOSED,
 * since failing open would recreate the thundering-herd problem this
 * exists to prevent).
 */
async function tryAcquireLocalStorageLock(tabId) {
  const lockId = generateLockId(tabId);
  try {
    const now = Date.now();
    const current = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );

    if (current?.expiresAt > now && String(current?.tabId) !== String(tabId)) {
      return null;
    }

    localStorage.setItem(
      REFRESH_LOCK_KEY,
      JSON.stringify({
        tabId,
        lockId,
        expiresAt: now + REFRESH_LOCK_MS,
      })
    );

    // Let competing tabs overwrite; only the last writer should proceed.
    await sleep(LOCK_VERIFY_DELAY_MS + Math.floor(Math.random() * 50));

    const saved = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );
    return saved?.lockId === lockId ? lockId : null;
  } catch {
    // Storage unavailable/broken: fail closed. Better to occasionally skip
    // a refresh cycle (another tab, or the next tick, will pick it up)
    // than to have every tab believe it holds the lock.
    return null;
  }
}

/**
 * Extend this tab's lock ownership, but only if it still owns it.
 * Used to keep the lock alive while a slow refresh is in flight.
 */
function renewLocalStorageLock(tabId, lockId) {
  try {
    const now = Date.now();
    const current = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );
    if (current?.lockId !== lockId) {
      return false; // lost ownership (e.g. expired and taken by another tab)
    }
    localStorage.setItem(
      REFRESH_LOCK_KEY,
      JSON.stringify({ tabId, lockId, expiresAt: now + REFRESH_LOCK_MS })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Release the lock, but only if this call is releasing the lockId it
 * actually acquired. Prevents an overlapping/late release from
 * deleting a *different* in-flight lock held by the same tab.
 */
export function releaseTokenRefreshLock(tabId, lockId) {
  try {
    const current = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );
    if (current?.lockId === lockId && String(current?.tabId) === String(tabId)) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Run `fn` in at most one tab. Prefers navigator.locks; falls back to localStorage.
 * @returns {Promise<boolean>} true if this tab ran the refresh
 */
export async function runWithTokenRefreshLock(tabId, fn) {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    let ran = false;
    await navigator.locks.request(
      WEB_LOCK_NAME,
      { ifAvailable: true },
      async (lock) => {
        if (!lock) {
          return;
        }
        ran = true;
        await fn();
      }
    );
    return ran;
  }

  const lockId = await tryAcquireLocalStorageLock(tabId);
  if (!lockId) {
    return false;
  }

  // Keep the lock alive for as long as fn() is actually running, so a
  // slow refresh doesn't outlive REFRESH_LOCK_MS and let another tab
  // start a second, overlapping refresh.
  const renewTimer = setInterval(() => {
    renewLocalStorageLock(tabId, lockId);
  }, LOCK_RENEW_MS);

  try {
    await fn();
    return true;
  } finally {
    clearInterval(renewTimer);
    releaseTokenRefreshLock(tabId, lockId);
  }
}